import { Producer } from "./producer";
import ProducerConfiguration, { ProducerDefaults } from "./producerConfiguration";
import { AxiosError } from "axios";
import ValidationError from "common/validationError";
import ProducerBulkUploadResponse from "./uploadProducersResponse"
import { UpdateAllProducersRequest } from "./updateAllProducers";
import ThrowsValidationError from "common/http/throwsValidationError";
import Throttle from "common/decorators/throttle"
import ReturnsNullOn404 from "common/http/returnsNullOn404";
import PagedData, { toPagedData } from "common/pagedData";
import ProducerInvitation from "./producerInvitation";
import ListItemsResponse from "common/listItemsResponse";
import HttpService from "common/http/httpService";

class ProducerService extends HttpService {

  @Throttle(1000, true, false)
  async getProducerConfiguration(): Promise<ProducerConfiguration> {
    const response = await this.getClient().get<ProducerConfiguration>(
      "/settings/v1/configuration/producers"
    );

    return response.data;
  }

  async listProducers(location?: string, trader?: string, status?: string): Promise<Producer[]> {

    const response = await this.getClient().get<Producer[]>('/settings/v1/producers', {
      params: {
        location: location,
        trader: trader,
        status: status
      }
    })

    return response.data;
  }

  async listProducersV2(pageSize: number, offset: number, location?: string, trader?: string, search?: string, searchProducerAccounts?: boolean, status?: string): Promise<PagedData<Producer>> {

    const response = await this.getClient().get<ListItemsResponse<Producer>>('/settings/v2/producers', {
      params: {
        pageSize,
        offset,
        location,
        trader,
        search,
        searchProducerAccounts,
        status: status
      }
    })

    return toPagedData(response.data);
  }

  @ThrowsValidationError()
  async createProducer(producer: Producer): Promise<Producer> {
      const response = await this.getClient().post<Producer>("/settings/v1/producers", producer);
      return response.data;
  }

  async createProducerInvitation(producerId: string, producerInvitation: ProducerInvitation): Promise<any> {
      await this.getClient().post<Producer>(`/settings/v1/producers/${producerId}/invite`, producerInvitation);
  }

  @ThrowsValidationError()
  async updateProducer(producer: Producer): Promise<any> {
      await this.getClient().put<Producer>(
        `/settings/v1/producers/${producer.id}`,
        producer);
  }

  @ThrowsValidationError()
  async updateAllProducers(allProducersRequest:UpdateAllProducersRequest): Promise<any> {
      const response = await this.getClient().put<Producer[]>(
        '/settings/v1/producers/all', allProducersRequest);

      return response.data;
  }

  @ThrowsValidationError()
  async updateProducerContact(producer: Producer): Promise<any> {
    const request = {
      phone: producer.phone,
      AdditionalEmails: producer.additionalEmails,
      AdditionalPhones: producer.additionalPhones
    };
    await this.getClient().put(`gateway/v1/users/me/customer-contact`, request);
  }

  @ThrowsValidationError()
  async deleteProducer(producer: Producer): Promise<any> {
      await this.getClient().delete<String>(`/settings/v1/producers/${producer.id}`);
  }
  
  async uploadProducers(selectedFile: any): Promise<any> {
    try {
      let formData = new FormData();
      formData.append("formFile", selectedFile)
      const response = await this.getClient().post<any>(
        '/settings/v1/producers/validate-bulk-import', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    catch (error) {
      const response = (error as AxiosError).response;
      if (response.status === 400) {
        const payload: any = response.data;
        if (payload.errorType != null || payload.errors != null) {
          throw new ValidationError(payload.errors)
        }
        else {
          return payload;
        }
      }
      return response.data;
    }
  }

  @ThrowsValidationError()
  async validateAccounts(producer: Producer): Promise<any> {
    const response = await this.getClient().post<string[]>("/settings/v1/producers/validate-accounts", {
      producerId: producer.id,
      firstName: producer.firstName,
      lastName: producer.lastName,
      businessName: producer.businessName,
      useBusinessName: producer.useBusinessName,
      isNewProducer: producer.id == null || producer.id.length === 0,
      accounts: producer.accounts
    });
    return response.data;
  }

  async importProducers(selectedFile: any): Promise<any> {
    try {
      let formData = new FormData();
      formData.append("formFile", selectedFile)
      const response = await this.getClient().post<ProducerBulkUploadResponse>(
        '/settings/v1/producers/bulk-import', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    catch (error) {
      const response = (error as AxiosError).response;
      if (response.status === 400) {
        const payload: any = response.data;
        if (payload.errorType != null || payload.errors != null) {
          throw new ValidationError(payload.errors)
        }
        else {
          return payload;
        }
      }
    }
  }

  @ThrowsValidationError()
  async updateProducerDefaults(producerDefaults: ProducerDefaults): Promise<any> {
      await this.getClient().put<ProducerDefaults>(
        '/settings/v1/producer-defaults', producerDefaults);
  }

  @ReturnsNullOn404()
  async getProducer(id: string): Promise<Producer> {
      const response = await this.getClient().get<Producer>(`/settings/v1/producers/${id}`);
      return response.data;
  }

  @ReturnsNullOn404()
  async getMyProducer(): Promise<Producer> {
      const response = await this.getClient().get<Producer>('/settings/v1/producers/me');
      return response.data;
  }

  @ReturnsNullOn404()
  async getCustomerInfo(): Promise<Producer> {
      const response = await this.getClient().get<Producer>('gateway/v2/users/me/customer-info');
      return response.data;
  }
}

const producerService = new ProducerService();
export default producerService;
